import React from 'react';
import { PartidoInfo } from '@/types/partido';
import { Card } from './Card';
import { ExternalLink, MapPin, Trophy } from 'lucide-react';
import { abrirEnMapas, hayUbicacion } from '@/utils/abrirEnMapas';

interface PartidoInfoCardProps {
  partidoInfo: PartidoInfo;
  onClick?: () => void;
  className?: string;
}

/** Escudo del equipo, con hueco reservado para que la fila no salte si falla la imagen. */
const Escudo: React.FC<{ url?: string; alt: string }> = ({ url, alt }) => (
  <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center">
    {url ? (
      <img
        src={url}
        alt={alt}
        className="max-w-full max-h-full object-contain drop-shadow"
        loading="lazy"
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
      />
    ) : (
      <div className="w-7 h-7 rounded-full bg-slate-700/40 border border-white/10" />
    )}
  </div>
);

export const PartidoInfoCard: React.FC<PartidoInfoCardProps> = ({
  partidoInfo,
  onClick,
  className = ''
}) => {
  const jugado = partidoInfo.estado === '1';
  const hayMapa = hayUbicacion(partidoInfo.latitud, partidoInfo.longitud);

  const obtenerFechaHoraVisual = (): { fecha: string; hora: string } => {
    try {
      const fecha = new Date(partidoInfo.fechaHora);
      return {
        fecha: fecha.toLocaleDateString('es-ES', {
          weekday: 'short', day: '2-digit', month: 'short'
        }),
        hora: fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
      };
    } catch {
      return {
        fecha: partidoInfo.fechaOriginal ?? partidoInfo.fechaHora,
        hora: partidoInfo.horaOriginal ?? ''
      };
    }
  };

  const { fecha, hora } = obtenerFechaHoraVisual();

  const abrirMapa = (e: React.MouseEvent) => {
    e.stopPropagation();   // el clic de la tarjeta abre el acta: aquí no queremos eso
    abrirEnMapas(partidoInfo.latitud, partidoInfo.longitud, partidoInfo.campo);
  };

  /** Una fila de equipo: escudo, nombre y marcador. La nuestra va resaltada. */
  const FilaEquipo: React.FC<{
    nombre: string;
    escudo?: string;
    goles?: number;
    nuestro: boolean;
    acento: string;
  }> = ({ nombre, escudo, goles, nuestro, acento }) => (
    <div
      className={`relative flex items-center gap-3 overflow-hidden rounded-xl p-2.5 border transition-colors ${
        nuestro ? 'bg-primary-500/10 border-primary-500/40' : 'bg-slate-800/10 border-white/5'
      }`}
    >
      <div className={`absolute top-0 left-0 w-1 h-full ${acento}`} />
      <Escudo url={escudo} alt={nombre} />
      <h3
        className={`flex-1 text-sm leading-tight drop-shadow-md ${
          nuestro ? 'font-extrabold text-white' : 'font-semibold text-slate-200'
        }`}
      >
        {nombre}
      </h3>
      {jugado && (
        <span className="text-xl font-black text-white tabular-nums drop-shadow">
          {goles ?? 0}
        </span>
      )}
    </div>
  );

  return (
    <div className={`relative group ${className}`}>
      <Card
        variant="glass"
        className={`relative h-full border-emerald-500/30 overflow-hidden ${onClick ? 'cursor-pointer' : ''}`}
        onClick={onClick}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

        <div className="p-4 relative z-10">

          {/* Header: competición a la izquierda, fecha y hora a la derecha */}
          <div className="flex justify-between items-start gap-3 mb-4">
            <div className="flex items-center gap-2 min-w-0 pt-0.5">
              <div className="p-1.5 bg-primary-500/10 rounded-lg backdrop-blur-sm border border-primary-500/20 flex-shrink-0">
                <Trophy className="w-3.5 h-3.5 text-primary-400" />
              </div>
              <span className="text-xs font-semibold text-primary-400 tracking-wide uppercase drop-shadow-sm truncate">
                {partidoInfo.competicionLabel ?? 'Competición'}
              </span>
            </div>

            <div className="flex-shrink-0 text-right">
              <p className="text-[11px] font-medium text-slate-300 capitalize leading-tight drop-shadow-sm">
                {fecha}
              </p>
              {hora && (
                <p className="text-xl font-black text-white tabular-nums leading-tight drop-shadow-md">
                  {hora}
                </p>
              )}
            </div>
          </div>

          {/* Enfrentamiento */}
          <div className="flex flex-col gap-2 mb-4">
            <FilaEquipo
              nombre={partidoInfo.equipoLocal}
              escudo={partidoInfo.escudoLocal}
              goles={partidoInfo.golesLocal}
              nuestro={partidoInfo.esLocal === true}
              acento="bg-primary-500"
            />

            {!jugado && (
              <div className="flex justify-center -my-1 z-10">
                <span className="bg-slate-900/20 backdrop-blur text-slate-400 text-[10px] font-black px-1.5 py-0.5 rounded border border-white/10 shadow-lg">
                  VS
                </span>
              </div>
            )}

            <FilaEquipo
              nombre={partidoInfo.equipoVisitante}
              escudo={partidoInfo.escudoVisitante}
              goles={partidoInfo.golesVisitante}
              nuestro={partidoInfo.esLocal === false}
              acento="bg-emerald-600"
            />
          </div>

          {/* Footer: jornada a la izquierda, campo pulsable a la derecha */}
          <div className="flex items-center justify-between gap-3 pt-3 border-t border-white/10">
            {partidoInfo.jornada ? (
              <span className="flex-shrink-0 bg-slate-800/40 backdrop-blur-sm border border-white/10 text-slate-300 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                Jornada {partidoInfo.jornada}
              </span>
            ) : (
              <span />
            )}

            {partidoInfo.campo && (
              hayMapa ? (
                <button
                  type="button"
                  onClick={abrirMapa}
                  className="flex-1 min-w-0 flex items-center gap-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-2.5 py-2 text-left transition-all hover:bg-emerald-500/20 active:bg-emerald-500/25 active:scale-[0.98]"
                >
                  <MapPin className="w-4 h-4 text-emerald-400 flex-shrink-0 drop-shadow-[0_0_5px_rgba(16,185,129,0.5)]" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] leading-tight font-medium text-slate-200 line-clamp-2">
                      {partidoInfo.campo}
                    </p>
                    <p className="text-[9px] font-bold text-emerald-400 uppercase tracking-wide mt-0.5">
                      Cómo llegar
                    </p>
                  </div>
                  <ExternalLink className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                </button>
              ) : (
                <div className="flex-1 min-w-0 flex items-center gap-2 px-2.5 py-2">
                  <MapPin className="w-4 h-4 text-slate-500 flex-shrink-0" />
                  <p className="text-[10px] leading-tight text-slate-400 line-clamp-2">
                    {partidoInfo.campo}
                  </p>
                </div>
              )
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};